import { decodeJwtPayload } from '../auth/jwtPayload';

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

/** Lowercase trim for stable UUID/string compare. */
export function normalizeOwnerIdComparable(v: unknown): string | null {
  if (typeof v === 'string') {
    const t = v.trim();
    return t ? t.toLowerCase() : null;
  }
  if (typeof v === 'number' && Number.isFinite(v)) {
    return String(v);
  }
  return null;
}

const JWT_OWNER_CLAIM_KEYS = [
  'owner_id',
  'user_id',
  'userid',
  'UserId',
  'id',
  'sub',
] as const;

const ASSET_OWNER_FIELD_KEYS = ['owner_id', 'ownerId', 'user_id', 'userId'] as const;

/**
 * Best-effort owner id on an asset row / GET payload.
 * If the API omits these, {@link canMutateAssetAsSessionOwner} treats the row as “unknown” and allows UI mutations.
 */
export function extractAssetOwnerId(asset: Record<string, unknown>): string | null {
  for (const key of ASSET_OWNER_FIELD_KEYS) {
    const n = normalizeOwnerIdComparable(asset[key]);
    if (n) {
      return n;
    }
  }
  for (const nestedKey of ['owner', 'asset_owner'] as const) {
    const nested = asset[nestedKey];
    if (!isPlainObject(nested)) {
      continue;
    }
    for (const key of ['id', 'user_id', 'owner_id', 'userId', 'ownerId'] as const) {
      const n = normalizeOwnerIdComparable(nested[key]);
      if (n) {
        return n;
      }
    }
  }
  return null;
}

/**
 * Identity from the persisted login access JWT (same source as Authorization for session logins).
 */
export function extractSessionOwnerIdFromAccessToken(token: string | null | undefined): string | null {
  const t = token?.trim();
  if (!t) {
    return null;
  }
  const payload = decodeJwtPayload(t);
  if (!payload) {
    return null;
  }
  for (const key of JWT_OWNER_CLAIM_KEYS) {
    const n = normalizeOwnerIdComparable(payload[key]);
    if (n) {
      return n;
    }
  }
  return null;
}

/**
 * When **`asset`** carries an owner id, require it to match the session JWT identity for edit/delete.
 * When owner id is **missing**, allow (backward compatible with APIs that omit it on “my list” payloads).
 */
export function canMutateAssetAsSessionOwner(
  asset: Record<string, unknown>,
  sessionAccessToken: string | null | undefined,
): boolean {
  const expected = extractAssetOwnerId(asset);
  if (!expected) {
    return true;
  }
  const actual = extractSessionOwnerIdFromAccessToken(sessionAccessToken);
  if (!actual) {
    return false;
  }
  return expected === actual;
}
